/**
 * Careers job-listing API client.
 *
 * Data source: our own server endpoint (config.careersJobsPath), same-origin —
 * a Netlify Function in production, src/setupProxy.js in local dev. That server
 * owns the Keka Hire integration and token; the frontend never sees Keka
 * credentials (PRD FR-2). The payload is the already-mapped { jobs: [...] }
 * internal contract, which jobsAdapter normalises into the view-model.
 *
 * Resilience: every successful fetch is cached in localStorage. If a later
 * fetch fails (offline, server/Keka down, network error), we serve the last
 * cached listing (if fresh and non-empty) so the page keeps showing jobs
 * instead of an error. The cache is refreshed on every successful fetch.
 */
import { config } from "../../../../../utils/config";
import { adaptJobsResponse } from "./jobsAdapter";

const CACHE_KEY = "np_careers_jobs_v1";
// A cached listing older than this is considered too stale to trust as a
// fallback (we'd rather show the error state than present a months-old list).
const MAX_CACHE_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * Read the last cached view-model listing, or null if none/unreadable/stale.
 * A cache older than MAX_CACHE_AGE_MS is treated as no cache.
 */
const readCache = () => {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.jobs)) return null;
    const age = Date.now() - (parsed.cachedAt || 0);
    if (age > MAX_CACHE_AGE_MS) return null; // too stale to trust
    return parsed.jobs;
  } catch (e) {
    // Private mode / corrupt entry / no localStorage — treat as no cache.
    return null;
  }
};

/** Persist the latest good listing (best-effort; ignores quota/private mode). */
const writeCache = (jobs) => {
  try {
    window.localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ jobs, cachedAt: Date.now() })
    );
  } catch (e) {
    /* storage unavailable — the app still works, just without a fallback. */
  }
};

/**
 * Fetch listable jobs and return them as view-model objects (see jobsAdapter).
 * On success: refreshes the cache and returns fresh jobs.
 * On failure: falls back to the last cached listing if one exists; otherwise
 * rethrows so the caller can render the error state.
 */
export const fetchJobs = async () => {
  try {
    // Same-origin, relative path — the endpoint is served by our own function
    // (Netlify) / setupProxy (local) on this site's origin. NOT prefixed with
    // config.apiUrl, which points at the external backend for other features.
    const res = await fetch(config.careersJobsPath, {
      headers: { Accept: "application/json" },
    });
    if (!res.ok) {
      throw new Error(`Failed to load jobs (${res.status})`);
    }
    const payload = await res.json();
    const jobs = adaptJobsResponse(payload);
    // Cache any well-formed response — including an empty one — so the cache
    // always reflects the last authoritative answer. This means a legitimate
    // "0 open roles" clears a stale non-empty cache (no closed roles resurface
    // on a later failure). A malformed 200 (no `jobs` array) is ignored, so it
    // can't clobber a known-good backup.
    if (payload && Array.isArray(payload.jobs)) {
      writeCache(jobs);
    }
    return jobs;
  } catch (err) {
    const cached = readCache();
    // Only fall back to a cache that actually has jobs; an empty/absent cache
    // lets the real error surface (honest error state) instead of a misleading
    // "no roles" listing.
    if (cached && cached.length > 0) {
      return cached;
    }
    throw err;
  }
};
